import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History, Calendar, Search, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Collection {
  name: string;
  description: string;
}

interface SearchHistoryItem {
  id: string;
  collection_name: string;
  search_text: string;
  created_at: string;
  results: any;
}

interface SearchHistoryProps {
  onBack: () => void;
  onSelectHistory: (item: SearchHistoryItem) => void;
}

export function SearchHistory({ onBack, onSelectHistory }: SearchHistoryProps) {
  const { user, session } = useAuth();
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && session) {
      fetchCollections();
      fetchHistory();
    }
  }, [user, session]);

  const fetchCollections = async () => {
    try {
      const response = await fetch('https://v2.api.doctia.com.ar/schemas', {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch collections');
      }
      
      const data = await response.json();
      setCollections(data.schemas || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('search_history')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Error al cargar el historial de búsquedas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCollectionLabel = (value: string) => {
    // Use the API-fetched collections like in SearchInterface
    const collection = collections.find(c => c.name === value);
    return collection?.description || value;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack} size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a Buscar
        </Button>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <History className="h-6 w-6 text-primary" />
          Historial de Búsquedas
        </h2>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          Cargando historial de búsquedas...
        </div>
      ) : history.length === 0 ? (
        <Card className="shadow-card border-border/50 bg-card/80">
          <CardContent className="p-8 text-center">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Aún no tenés búsquedas</h3>
            <p className="text-muted-foreground">
              Tus búsquedas aparecerán aquí una vez que comiences a usar DoctIA.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {history.map((item) => (
            <Card 
              key={item.id} 
              className="shadow-card border-border/50 bg-card/80 backdrop-blur-sm hover:shadow-elegant transition-all duration-300 cursor-pointer"
              onClick={() => onSelectHistory(item)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <CardTitle className="text-lg">
                    {getCollectionLabel(item.collection_name)}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <ScrollArea className="h-16">
                  <p className="text-muted-foreground text-sm mb-3">
                    {item.search_text}
                  </p>
                </ScrollArea>
                
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-xs">
                    {Array.isArray(item.results) ? item.results.length : 0} resultados
                  </Badge>
                  <Button variant="ghost" size="sm" className="text-xs">
                    Ver Resultados
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}